import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";
import { requireAdminOrModerator } from "./auth-roles.server";

const inputSchema = z.object({
  brand: z.string().min(1).max(50),
  model: z.string().min(1).max(50),
  variant: z.string().max(100).optional().default(""),
  year: z.number().int().min(1900).max(2100),
  mileage: z.number().int().min(0).max(2_000_000),
  fuel_type: z.string().max(30).optional().default(""),
  transmission: z.string().max(30).optional().default(""),
  color: z.string().max(30).optional().default(""),
  body_type: z.string().max(30).optional().default(""),
  doors: z.number().int().min(0).max(10).optional().default(0),
  seats: z.number().int().min(0).max(20).optional().default(0),
  engine_size: z.string().max(20).optional().default(""),
  horsepower: z.number().int().min(0).max(2000).optional().default(0),
  equipment: z.string().max(3000).optional().default(""),
  tone: z.enum(["sælgende", "neutral", "luksus"]).optional().default("sælgende"),
});

export type CarDescriptionInput = z.infer<typeof inputSchema>;
export type CarDescriptionResult = {
  error: string | null;
  description: string | null;
  seo_title: string | null;
  seo_description: string | null;
};

/**
 * Pure handler for AI car-description generation. Extracted from the
 * createServerFn wrapper so unit tests can mock the LOVABLE_API_KEY env
 * and the AI gateway fetch without going through the auth middleware.
 *
 * Auth + role guard are enforced by the createServerFn wrapper below — this
 * handler assumes the caller has already been authorized.
 */
export async function generateCarDescriptionHandler(
  data: CarDescriptionInput,
  apiKey: string | undefined,
  fetchImpl: typeof fetch = fetch,
): Promise<CarDescriptionResult> {
  if (!apiKey) {
    return { error: "AI er ikke konfigureret", description: null, seo_title: null, seo_description: null };
  }

  const equipmentList = data.equipment
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 50);

  const carSpecs = [
    `Mærke: ${data.brand}`,
    `Model: ${data.model}`,
    data.variant && `Variant: ${data.variant}`,
    `Årgang: ${data.year}`,
    `Kilometerstand: ${data.mileage.toLocaleString("da-DK")} km`,
    data.fuel_type && `Brændstof: ${data.fuel_type}`,
    data.transmission && `Gearkasse: ${data.transmission}`,
    data.body_type && `Karrosseri: ${data.body_type}`,
    data.color && `Farve: ${data.color}`,
    data.doors && `Døre: ${data.doors}`,
    data.seats && `Sæder: ${data.seats}`,
    data.engine_size && `Motor: ${data.engine_size}`,
    data.horsepower && `Hestekræfter: ${data.horsepower} hk`,
    equipmentList.length > 0 && `Udstyr: ${equipmentList.join(", ")}`,
  ]
    .filter(Boolean)
    .join("\n");

  const toneInstruction = {
    sælgende: "Brug en entusiastisk og sælgende tone, der fremhæver bilens stærke sider og lokker køberen.",
    neutral: "Brug en saglig, informativ og neutral tone uden overdreven markedsføring.",
    luksus: "Brug en eksklusiv, sofistikeret tone der appellerer til premium-segmentet.",
  }[data.tone];

  const systemPrompt = `Du er en professionel dansk biltekstforfatter for AutoXChange. Du skriver salgsbeskrivelser til brugte biler.

REGLER:
- Skriv ALTID på dansk
- ${toneInstruction}
- Beskrivelsen skal være 120-200 ord, opdelt i 2-3 letlæselige afsnit (brug \\n\\n mellem afsnit)
- Start med en stærk indledning der nævner mærke, model og årgang
- Fremhæv 3-5 mest relevante udstyrsdetaljer naturligt i teksten
- Nævn kilometerstand og generel stand
- Slut med en handlingsorienteret afslutning (fx "Kontakt os for prøvekørsel")
- Brug IKKE markdown, fed skrift eller emojis – kun ren tekst
- Opfind ALDRIG specifikationer eller udstyr der ikke står i listen
- Brug danske decimaltegn og talformater

Du skal også generere:
- seo_title: max 60 tegn, indeholder mærke + model + årgang
- seo_description: max 155 tegn, sælgende meta-beskrivelse

Returnér KUN JSON i dette format (ingen markdown, ingen kodeblokke):
{"description": "...", "seo_title": "...", "seo_description": "..."}`;

  try {
    const response = await fetchImpl("https://ai.gateway.lovable.dev/v1/chat/completions", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        model: "google/gemini-3-flash-preview",
        messages: [
          { role: "system", content: systemPrompt },
          { role: "user", content: `Generér salgsbeskrivelse for denne bil:\n\n${carSpecs}` },
        ],
        tools: [
          {
            type: "function",
            function: {
              name: "save_description",
              description: "Returnér den genererede beskrivelse og SEO-data",
              parameters: {
                type: "object",
                properties: {
                  description: { type: "string", description: "Salgsbeskrivelsen på dansk, 120-200 ord, 2-3 afsnit" },
                  seo_title: { type: "string", description: "SEO titel, max 60 tegn" },
                  seo_description: { type: "string", description: "SEO meta description, max 155 tegn" },
                },
                required: ["description", "seo_title", "seo_description"],
                additionalProperties: false,
              },
            },
          },
        ],
        tool_choice: { type: "function", function: { name: "save_description" } },
      }),
    });

    if (!response.ok) {
      if (response.status === 429) {
        return { error: "For mange forespørgsler – prøv igen om lidt", description: null, seo_title: null, seo_description: null };
      }
      if (response.status === 402) {
        return { error: "AI-kreditter er opbrugt. Tilføj kreditter i Workspace settings.", description: null, seo_title: null, seo_description: null };
      }
      const t = await response.text();
      console.error("AI gateway error:", response.status, t);
      return { error: "AI service fejlede", description: null, seo_title: null, seo_description: null };
    }

    const json = await response.json();
    const toolCall = json.choices?.[0]?.message?.tool_calls?.[0];
    if (!toolCall?.function?.arguments) {
      return { error: "AI returnerede ikke et gyldigt svar", description: null, seo_title: null, seo_description: null };
    }

    const parsed = JSON.parse(toolCall.function.arguments);
    return {
      error: null,
      description: parsed.description as string,
      seo_title: parsed.seo_title as string,
      seo_description: parsed.seo_description as string,
    };
  } catch (err) {
    console.error("Generate description error:", err);
    return { error: "Uventet fejl", description: null, seo_title: null, seo_description: null };
  }
}

export const generateCarDescription = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => inputSchema.parse(input))
  .handler(async ({ data, context }) => {
    // Role guard: kun admin/moderator må generere beskrivelser
    try {
      await requireAdminOrModerator(context.userId);
    } catch {
      throw new Response("Forbidden: admin or moderator role required", { status: 403 });
    }

    return generateCarDescriptionHandler(data, process.env.LOVABLE_API_KEY);
  });
